import React, { useState } from 'react';
import { 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaLink, 
  FaUsers, 
  FaBriefcase, 
  FaTools, 
  FaCheckCircle, 
  FaArrowRight, 
  FaAward 
} from 'react-icons/fa';

// Mock Company Data mapping cleanly to tbl_entity
const MOCK_COMPANY = {
  name: "Prisma Cloud Technologies",
  handle: "prismacloud",
  verifiedBadge: true,
  location: "San Francisco, CA & Remote",
  website: "https://prismacloud.io",
  employeesCount: 142,
  description: "Next-generation database infrastructure and automated compliance systems built for high-throughput scaling.",
  requiredStack: ["PostgreSQL", "Knex.js", "AWS Lambda", "Docker"]
};

// Mock Employee Directory for the company profile
const MOCK_EMPLOYEES = [
  { name: "KD", handle: "kdave", role: "Principal Architect", matchRate: "100%", avatar: "KD", topSkills: ["Knex.js", "PostgreSQL", "LLM Structured Extraction"] },
  { name: "Elena Rostova", handle: "elena_r", role: "Senior Backend Engineer", matchRate: "92%", avatar: "ER", topSkills: ["PostgreSQL", "AWS Lambda", "Node.js"] },
  { name: "Marcus Sterling", handle: "msterling", role: "DevOps Lead", matchRate: "88%", avatar: "MS", topSkills: ["Docker", "Terraform", "AWS Cloud Architecture"] }
];

// Open positions with live skill match analyzer tags
const MOCK_ROLES = [
  {
    title: "Senior Database Architect",
    department: "Core Engineering",
    type: "Remote / Full-time",
    matchPercentage: 94,
    skillsGap: ["No gaps detected! Excellent fit."]
  },
  {
    title: "Lead Backend Developer (AI Pipelines)",
    department: "Data Platform Group",
    type: "Hybrid SF / Full-time",
    matchPercentage: 78,
    skillsGap: ["Missing: LLM Structured Extraction", "Missing: Gemini API"]
  }
];

export default function CompanyExplorer() {
  const [activeTab, setActiveTab] = useState('employees');

  return (
    <div style={styles.container}>
      {/* Header Banner - Repo/Org Style */}
      <div style={styles.orgHeader}>
        <div style={styles.headerLimit}>
          <div style={styles.logoAndMeta}>
            <div style={styles.orgLogoPlaceholder}>
              <FaBuilding />
            </div>
            <div style={styles.orgIdentity}>
              <div style={styles.titleRow}>
                <h1 style={styles.orgName}>{MOCK_COMPANY.name}</h1>
                {MOCK_COMPANY.verifiedBadge && <FaCheckCircle style={styles.verifiedBadgeIcon} title="Verified Entity" />}
              </div>
              <span style={styles.orgHandle}>@{MOCK_COMPANY.handle}</span>
            </div>
          </div>
          
          <p style={styles.orgDescription}>{MOCK_COMPANY.description}</p>
          
          <div style={styles.metadataBar}>
            <span style={styles.metaItem}><FaMapMarkerAlt /> {MOCK_COMPANY.location}</span>
            <span style={styles.metaItem}><FaLink /> <a href={MOCK_COMPANY.website} style={styles.link}>{MOCK_COMPANY.website.replace("https://", "")}</a></span>
            <span style={styles.metaItem}><FaUsers /> {MOCK_COMPANY.employeesCount} platform employees</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Sidebar Stack vs Tabbed Content */}
      <div style={styles.mainLayout}>
        
        {/* Left Column: Tech Footprint Panel */}
        <aside style={styles.leftSidebar}>
          <div style={styles.cardHeaderBox}>
            <FaTools style={styles.cardHeaderIcon} />
            <h3 style={styles.cardTitleText}>Entity Tech Footprint</h3>
          </div>
          <p style={styles.sidebarDesc}>Consensus-verified core stack required across active workspace repositories:</p>
          <div style={styles.techStackList}>
            {MOCK_COMPANY.requiredStack.map((tech, index) => (
              <div key={index} style={styles.techPillRow}>
                <span style={styles.techPillName}>{tech}</span>
                <span style={styles.requiredIndicator}>Core</span>
              </div>
            ))}
          </div>
        </aside>

        {/* Right Column: Interactive Tabs */}
        <main style={styles.rightContentArea}>
          <nav style={styles.tabNavStrip}>
            <button 
              onClick={() => setActiveTab('employees')} 
              style={{ ...styles.tabBtn, ...(activeTab === 'employees' ? styles.activeTabBtn : {}) }}
            >
              <FaUsers style={{ marginRight: '6px' }} /> Platform Employees ({MOCK_EMPLOYEES.length})
            </button>
            <button 
              onClick={() => setActiveTab('roles')} 
              style={{ ...styles.tabBtn, ...(activeTab === 'roles' ? styles.activeTabBtn : {}) }}
            >
              <FaBriefcase style={{ marginRight: '6px' }} /> Open Positions ({MOCK_ROLES.length})
            </button>
          </nav>

          {/* Employees List View */}
          {activeTab === 'employees' && (
            <div style={styles.listContainer}>
              {MOCK_EMPLOYEES.map((employee, idx) => (
                <div key={idx} style={styles.talentItemCard}>
                  <div style={styles.talentProfileHeader}>
                    <div style={styles.talentAvatar}>{employee.avatar}</div>
                    <div style={styles.talentMeta}>
                      <div style={styles.talentNameRow}>
                        <span style={styles.talentRealName}>{employee.name}</span>
                        <span style={styles.talentHandle}>@{employee.handle}</span>
                      </div>
                      <span style={styles.talentRoleText}>{employee.role}</span>
                    </div>
                    <div style={styles.matchBadge}>
                      <FaAward style={{ marginRight: '4px', color: '#d29922' }} /> {employee.matchRate} Stack Match
                    </div>
                  </div>
                  
                  <div style={styles.employeeTalentStack}>
                    <span style={styles.stackLabel}>Verified Strengths:</span>
                    <div style={styles.talentPillGrid}>
                      {employee.topSkills.map((skill, index) => (
                        <span key={index} style={styles.verifiedSkillTag}>
                          <FaCheckCircle style={styles.tagCheck} /> {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Open Positions List View */}
          {activeTab === 'roles' && (
            <div style={styles.listContainer}>
              {MOCK_ROLES.map((role, idx) => (
                <div key={idx} style={styles.roleCard}>
                  <div style={styles.roleHeaderRow}>
                    <div style={styles.roleTitleGroup}>
                      <h4 style={styles.roleTitleText}>{role.title}</h4>
                      <span style={styles.roleDeptText}>{role.department} • {role.type}</span>
                    </div>
                    <div style={styles.matchScoreIndicator}>
                      <span style={styles.matchScoreNum}>{role.matchPercentage}%</span>
                      <span style={styles.matchScoreLabel}>Skill Match</span>
                    </div>
                  </div>

                  <div style={styles.roleAnalysisBlock}>
                    <span style={styles.analysisLabel}>Real-time Gap Analysis:</span>
                    <ul style={styles.analysisList}>
                      {role.skillsGap.map((gap, i) => (
                        <li key={i} style={gap.startsWith("Missing") ? styles.gapMissingItem : styles.gapClearItem}>
                          {gap}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={styles.roleActionRow}>
                    <button style={styles.actionButton}>
                      Apply with Live-Resume <FaArrowRight style={{ marginLeft: '6px' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    paddingBottom: '40px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  orgHeader: {
    backgroundColor: '#161b22',
    borderBottom: '1px solid #30363d',
    padding: '32px 24px',
    color: '#c9d1d9'
  },
  headerLimit: {
    maxWidth: '1240px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  logoAndMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  orgLogoPlaceholder: {
    width: '60px',
    height: '60px',
    borderRadius: '6px',
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    color: '#8b949e'
  },
  orgIdentity: {
    display: 'flex',
    flexDirection: 'column'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  orgName: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#f0f6fc',
    margin: 0
  },
  verifiedBadgeIcon: {
    color: '#58a6ff',
    fontSize: '18px'
  },
  orgHandle: {
    fontSize: '14px',
    color: '#8b949e',
    fontWeight: '500'
  },
  orgDescription: {
    margin: 0,
    fontSize: '15px',
    lineHeight: '1.5',
    color: '#c9d1d9',
    maxWidth: '720px'
  },
  metadataBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    fontSize: '13px',
    color: '#8b949e',
    borderTop: '1px solid #21262d',
    paddingTop: '16px'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  link: {
    color: '#58a6ff',
    textDecoration: 'none'
  },
  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '24px',
    maxWidth: '1240px',
    margin: '24px auto 0 auto',
    padding: '0 24px'
  },
  leftSidebar: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    height: 'fit-content'
  },
  cardHeaderBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  cardHeaderIcon: {
    color: '#8b949e',
    fontSize: '14px'
  },
  cardTitleText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  sidebarDesc: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: '1.4'
  },
  techStackList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  techPillRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0d1117',
    border: '1px solid #21262d',
    borderRadius: '4px',
    padding: '8px 12px'
  },
  techPillName: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#c9d1d9'
  },
  requiredIndicator: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#238636',
    backgroundColor: 'rgba(35, 134, 54, 0.1)',
    padding: '2px 6px',
    borderRadius: '10px'
  },
  rightContentArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  tabNavStrip: {
    display: 'flex',
    gap: '8px',
    borderBottom: '1px solid #30363d',
    paddingBottom: '8px'
  },
  tabBtn: {
    background: 'none',
    border: 'none',
    color: '#8b949e',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '6px'
  },
  activeTabBtn: {
    backgroundColor: '#21262d',
    color: '#58a6ff',
    fontWeight: '600'
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  talentItemCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  talentProfileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    position: 'relative'
  },
  talentAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#30363d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#f0f6fc'
  },
  talentMeta: {
    display: 'flex',
    flexDirection: 'column'
  },
  talentNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  talentRealName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc'
  },
  talentHandle: {
    fontSize: '12px',
    color: '#8b949e'
  },
  talentRoleText: {
    fontSize: '12px',
    color: '#8b949e',
    marginTop: '2px'
  },
  matchBadge: {
    position: 'absolute',
    right: 0,
    top: 4,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(210, 153, 34, 0.1)',
    color: '#d29922',
    border: '1px solid rgba(210, 153, 34, 0.15)',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600'
  },
  employeeTalentStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderTop: '1px solid #21262d',
    paddingTop: '12px',
    marginTop: '4px'
  },
  stackLabel: {
    fontSize: '11px',
    color: '#8b949e',
    textTransform: 'uppercase',
    fontWeight: '600'
  },
  talentPillGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  verifiedSkillTag: {
    fontSize: '11px',
    color: '#39d353',
    backgroundColor: 'rgba(35, 134, 54, 0.05)',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid rgba(35, 134, 54, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  tagCheck: {
    fontSize: '10px'
  },
  roleCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  roleHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  roleTitleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  roleTitleText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  roleDeptText: {
    fontSize: '12px',
    color: '#8b949e'
  },
  matchScoreIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'rgba(56, 139, 253, 0.1)',
    border: '1px solid rgba(56, 139, 253, 0.15)',
    padding: '8px 12px',
    borderRadius: '6px',
    minWidth: '90px'
  },
  matchScoreNum: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#58a6ff'
  },
  matchScoreLabel: {
    fontSize: '10px',
    color: '#8b949e',
    textTransform: 'uppercase',
    marginTop: '2px'
  },
  roleAnalysisBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderTop: '1px solid #21262d',
    paddingTop: '12px'
  },
  analysisLabel: {
    fontSize: '11px',
    color: '#8b949e',
    textTransform: 'uppercase',
    fontWeight: '600'
  },
  analysisList: {
    margin: 0,
    paddingLeft: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  gapMissingItem: {
    fontSize: '13px',
    color: '#f85149',
    listStyleType: 'square'
  },
  gapClearItem: {
    fontSize: '13px',
    color: '#39d353',
    listStyleType: 'disc'
  },
  roleActionRow: {
    borderTop: '1px solid #21262d',
    paddingTop: '12px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  actionButton: {
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#c9d1d9',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s'
  }
};